import type { UserAuditRule, UserAuditRuleDraft } from "./types";

/**
 * User audit rules + per-rule overrides + display order all live in the same
 * `localStorage`-backed storage as the formatting rules. See
 * src/rules/storage.ts for the migration / wrapper details.
 */

const USER_AUDIT_RULES_KEY = "cogseven.toolkit.userAuditRules";
const AUDIT_RULE_ORDER_KEY = "cogseven.toolkit.auditRuleOrder";
const SYSTEM_AUDIT_OVERRIDES_KEY = "cogseven.toolkit.systemAuditRuleOverrides";

export interface SystemAuditRuleOverride {
  enabled?: boolean;
}

function getLocal(key: string): string | null {
  return localStorage.getItem(key);
}

function setLocal(key: string, value: string): void {
  localStorage.setItem(key, value);
}

export async function loadUserAuditRules(): Promise<UserAuditRule[]> {
  const raw = getLocal(USER_AUDIT_RULES_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isValidUserAuditRule).map(normalize);
  } catch {
    return [];
  }
}

export async function saveUserAuditRules(rules: UserAuditRule[]): Promise<void> {
  setLocal(USER_AUDIT_RULES_KEY, JSON.stringify(rules));
}

export async function addUserAuditRule(draft: UserAuditRuleDraft): Promise<UserAuditRule> {
  const rule: UserAuditRule = {
    ...draft,
    id: generateId(),
    source: "user",
  };
  const rules = await loadUserAuditRules();
  rules.push(rule);
  await saveUserAuditRules(rules);
  return rule;
}

export async function updateUserAuditRule(
  id: string,
  updates: Partial<Omit<UserAuditRule, "id" | "source">>
): Promise<void> {
  const rules = await loadUserAuditRules();
  const idx = rules.findIndex((r) => r.id === id);
  if (idx === -1) throw new Error(`updateUserAuditRule: id not found (${id})`);
  rules[idx] = { ...rules[idx], ...updates, source: "user", id };
  await saveUserAuditRules(rules);
}

export async function deleteUserAuditRule(id: string): Promise<void> {
  const remaining = (await loadUserAuditRules()).filter((r) => r.id !== id);
  await saveUserAuditRules(remaining);
}

export async function loadAuditRuleOrder(): Promise<string[]> {
  const raw = getLocal(AUDIT_RULE_ORDER_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.every((x) => typeof x === "string") ? parsed : [];
  } catch {
    return [];
  }
}

export async function saveAuditRuleOrder(orderIds: string[]): Promise<void> {
  setLocal(AUDIT_RULE_ORDER_KEY, JSON.stringify(orderIds));
}

export async function loadSystemAuditRuleOverrides(): Promise<
  Record<string, SystemAuditRuleOverride>
> {
  const raw = getLocal(SYSTEM_AUDIT_OVERRIDES_KEY);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return {};
    const out: Record<string, SystemAuditRuleOverride> = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === "object" && value !== null) {
        out[key] = value as SystemAuditRuleOverride;
      }
    }
    return out;
  } catch {
    return {};
  }
}

export async function saveSystemAuditRuleOverrides(
  overrides: Record<string, SystemAuditRuleOverride>
): Promise<void> {
  setLocal(SYSTEM_AUDIT_OVERRIDES_KEY, JSON.stringify(overrides));
}

function generateId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `audit-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function normalize(rule: UserAuditRule): UserAuditRule {
  // Defensive: ensure required fields have sane defaults if older saved data
  // is missing them.
  return {
    ...rule,
    match: rule.match ?? {},
    message: rule.message ?? "matched",
    severity: rule.severity ?? "warning",
  };
}

function isValidUserAuditRule(x: unknown): x is UserAuditRule {
  if (typeof x !== "object" || x === null) return false;
  const r = x as Record<string, unknown>;
  if (typeof r.id !== "string") return false;
  if (typeof r.name !== "string") return false;
  if (typeof r.enabled !== "boolean") return false;
  if (r.source !== "user") return false;
  if (r.severity !== "info" && r.severity !== "warning" && r.severity !== "error") return false;
  // match and message default-fill in normalize(); accept missing.
  return true;
}
